import { useEffect, useState } from 'react';
import { Footer } from '../components/Footer';
import { settings as settingsApi, auth, UserSettings } from '../lib/api';
import { useAuth } from '../lib/auth';

export function Settings() {
  const { user, logout } = useAuth();
  const [s, setS] = useState<UserSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');

  useEffect(() => {
    settingsApi
      .get()
      .then(setS)
      .catch(() => setS({ fetch_method: 'backend', audio_quality: 'standard', mixer_open: false }));
  }, []);

  async function patch(p: Partial<UserSettings>) {
    if (!s) return;
    const next = { ...s, ...p };
    setS(next);
    setSaving(true);
    try {
      const updated = await settingsApi.update(p);
      setS(updated);
    } catch {
      // ignore — UI keeps optimistic state
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    try {
      await auth.deleteAccount();
      logout();
    } catch (e: any) {
      alert(e?.detail || 'Could not delete account.');
    }
  }

  if (!s) {
    return (
      <div className="rl-page" style={{ paddingTop: 40, textAlign: 'center' }}>
        <div className="rl-spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  }

  return (
    <div className="rl-page">
      <h1 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700 }}>Settings</h1>

      <div className="rl-card flat" style={{ padding: 18, marginBottom: 12 }}>
        <div className="rl-label" style={{ marginBottom: 4 }}>
          Account
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          {user?.picture_url && (
            <img
              src={user.picture_url}
              alt=""
              style={{ width: 36, height: 36, borderRadius: '50%' }}
            />
          )}
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{user?.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{user?.email}</div>
          </div>
        </div>
      </div>

      <Section label="Default fetch method">
        <RadioGroup
          value={s.fetch_method}
          options={[
            {
              value: 'backend',
              label: 'Standard (recommended)',
              hint: 'The most reliable way to load songs.',
            },
            {
              value: 'client',
              label: 'Direct (experimental)',
              hint: 'An alternate loader that can be faster on some networks.',
            },
          ]}
          onChange={(v) => patch({ fetch_method: v as UserSettings['fetch_method'] })}
        />
      </Section>

      <Section label="Default audio quality">
        <RadioGroup
          value={s.audio_quality}
          options={[
            { value: 'fast', label: 'Fast', hint: 'Quickest, lower-quality stems.' },
            { value: 'standard', label: 'Standard', hint: 'Balanced quality and speed.' },
            { value: 'high', label: 'High', hint: 'Cleanest stems, ~2× slower.' },
          ]}
          onChange={(v) => patch({ audio_quality: v as UserSettings['audio_quality'] })}
        />
      </Section>

      <Section label="Studio behavior">
        <ToggleRow
          checked={s.mixer_open}
          onChange={(v) => patch({ mixer_open: v })}
          label="Open mixer panel by default"
          hint="Show the 6-stem mixer when a separation finishes, instead of collapsed."
        />
      </Section>

      <div
        className="rl-card flat"
        style={{
          padding: 18,
          marginTop: 24,
          borderColor: 'rgba(239, 68, 68, 0.2)',
        }}
      >
        <div className="rl-label" style={{ marginBottom: 4, color: '#fca5a5' }}>
          Danger zone
        </div>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '8px 0 16px' }}>
          Deleting your account permanently removes your saved sections, billing history, and
          revokes your subscription. This cannot be undone.
        </p>
        {!confirmingDelete ? (
          <button
            className="rl-btn-secondary"
            onClick={() => setConfirmingDelete(true)}
            style={{ borderColor: 'rgba(239, 68, 68, 0.4)', color: '#fca5a5' }}
          >
            Delete my account
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ color: 'var(--text)', fontSize: 13, margin: 0 }}>
              Type <strong>DELETE</strong> to confirm:
            </p>
            <input
              type="text"
              className="rl-input"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="rl-btn-primary"
                disabled={deleteText !== 'DELETE'}
                onClick={handleDelete}
                style={{
                  background: deleteText === 'DELETE' ? '#ef4444' : 'rgba(239, 68, 68, 0.35)',
                  boxShadow: deleteText === 'DELETE' ? '0 0 18px rgba(239, 68, 68, 0.4)' : 'none',
                }}
              >
                Permanently delete
              </button>
              <button
                className="rl-btn-secondary"
                onClick={() => {
                  setConfirmingDelete(false);
                  setDeleteText('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {saving && (
        <div
          style={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            background: 'var(--panel)',
            border: '1px solid var(--border-strong)',
            padding: '8px 14px',
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--text-dim)',
          }}
        >
          Saving…
        </div>
      )}

      <Footer />
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rl-card flat" style={{ padding: 18, marginBottom: 12 }}>
      <div className="rl-label" style={{ marginBottom: 12 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

interface RadioOption {
  value: string;
  label: string;
  hint: string;
}

function RadioGroup({
  value,
  options,
  onChange,
}: {
  value: string;
  options: RadioOption[];
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {options.map((opt) => (
        <label
          key={opt.value}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '10px 12px',
            background: 'var(--panel-2)',
            border: `1px solid ${value === opt.value ? 'var(--accent-purple)' : 'var(--border)'}`,
            borderRadius: 6,
            cursor: 'pointer',
            transition: 'border-color 80ms ease',
          }}
        >
          <input
            type="radio"
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            style={{ marginTop: 3, accentColor: 'var(--accent-purple)', flexShrink: 0 }}
          />
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: value === opt.value ? 'var(--accent-purple)' : 'var(--text)',
              }}
            >
              {opt.label}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.4, marginTop: 2 }}>
              {opt.hint}
            </div>
          </div>
        </label>
      ))}
    </div>
  );
}

function ToggleRow({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        cursor: 'pointer',
      }}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        style={{
          width: 36,
          height: 20,
          background: checked ? 'var(--accent-gradient)' : 'rgba(255,255,255,0.1)',
          border: 'none',
          borderRadius: 999,
          position: 'relative',
          cursor: 'pointer',
          padding: 0,
          flexShrink: 0,
          marginTop: 2,
          boxShadow: checked ? '0 0 8px rgba(111,62,238,0.4)' : 'none',
          transition: 'all 120ms ease',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 18 : 2,
            width: 16,
            height: 16,
            background: '#fff',
            borderRadius: '50%',
            transition: 'left 120ms ease',
          }}
        />
      </button>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.4, marginTop: 2 }}>
          {hint}
        </div>
      </div>
    </label>
  );
}
